import { CustomLabel, CustomLabelType } from "types/CustomLabel";
import { EvaluationMetric } from "types/evaluationMetric";

export const LEVEL_COMPETENCY_OBJ = [
  "Pre-novice 1",
  "Pre-novice 2",
  "Novice",
  "Advanced beginner",
  "Competent",
  "Proficient",
];

export const LEVEL_EPA_OBJ = [
  "1 Direct supervision",
  "2 Indirect supervision",
  "3 Distant supervision",
  "4 On-demand supervision",
  "5 I can supervise others",
];

export const TARGET_ACHIEVEMENT_TIME_CLINIC = [
  "First 3 months",
  "First 6 months",
  "First 9 months",
  "End of Internship",
  "Post-Internship",
  "Post-Internship",
];

export const TARGET_ACHIEVEMENT_TIME_EDUCATIONAL = [
  "End of First year",
  "End of Second year",
  "End of Third year",
  "End of Fourth year",
  "End of Fifth year",
  "Post-Graduation",
];

/**
 * Precedence for skill-level display labels:
 * 1) label stored on the evaluation metric
 * 2) organization custom label
 * 3) hardcoded default
 *
 * Target Achievement Time uses org → default only (see getTargetAchievementTime).
 */
export const resolveEvaluationMetricLabel = (
  metricLabel?: string | null,
  orgLabel?: string | null,
  defaultLabel?: string | null,
): string => {
  const fromMetric = metricLabel?.trim();
  if (fromMetric) {
    return fromMetric;
  }
  const fromOrg = orgLabel?.trim();
  if (fromOrg) {
    return fromOrg;
  }
  return (defaultLabel || "").trim();
};

export const getRequirementEvaluationMetrics = (
  requirement?: {
    evaluation_metrics?: EvaluationMetric[] | null;
    evaluation_metric?: EvaluationMetric[] | null;
  } | null,
): EvaluationMetric[] =>
  requirement?.evaluation_metrics || requirement?.evaluation_metric || [];

export const stripLeadingSkillLevelScore = (
  label: string | null | undefined,
  score: number,
): string => {
  const trimmed = (label || "").trim();
  if (!trimmed) return "";
  const escaped = String(score).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const stripped = trimmed
    .replace(new RegExp(`^${escaped}(?:[.)\\]|\\s|[:-])+`), "")
    .trim();
  return stripped || trimmed;
};

export const formatSkillLevelLabel = (
  score: number,
  label: string | null | undefined,
): string => {
  const name = stripLeadingSkillLevelScore(label, score);
  return name ? `${score} ${name}` : String(score);
};

const hasText = (value?: string | null) => Boolean(value?.trim());

const isFilledEvaluationMetric = (metric: EvaluationMetric) =>
  Number.isFinite(Number(metric.skill_level)) &&
  (hasText(metric.skill_level_label) || hasText(metric.description));

export const getSkillLevelRatingOptions = (
  requirementType: string | null | undefined,
  evaluationMetrics: EvaluationMetric[] | null | undefined,
  customLabels: CustomLabel[] = [],
) => {
  const typeKey =
    requirementType === "EPA"
      ? CustomLabelType.SKILL_LEVEL_SCORE_EPA
      : CustomLabelType.SKILL_LEVEL_SCORE_COMPETENCY;

  const orgOptions = (customLabels || [])
    .filter((label) => label.type === typeKey && hasText(label.label))
    .sort((a, b) => a.index - b.index)
    .map((label) => ({ label: label.label, value: label.index }));
  const base =
    orgOptions.length > 0
      ? orgOptions
      : getDefaultCustomLabels(typeKey)
          .slice()
          .sort((a, b) => a.index - b.index)
          .map((label) => ({ label: label.label, value: label.index }));

  const metrics = (evaluationMetrics || []).filter(isFilledEvaluationMetric);
  const labelByLevel = new Map(base.map((option) => [option.value, option.label]));
  const uniqueMetrics = [
    ...metrics
      .reduce((byLevel, metric) => {
        const skillLevel = Number(metric.skill_level);
        const existing = byLevel.get(skillLevel);
        if (!existing || (!hasText(existing.skill_level_label) && hasText(metric.skill_level_label))) {
          byLevel.set(skillLevel, { ...metric, skill_level: skillLevel });
        }
        return byLevel;
      }, new Map<number, EvaluationMetric>())
      .values(),
  ].sort((a, b) => a.skill_level - b.skill_level);
  const options =
    uniqueMetrics.length > 0
      ? uniqueMetrics.map((metric) => ({
          value: metric.skill_level,
          label: resolveEvaluationMetricLabel(
            metric.skill_level_label,
            labelByLevel.get(metric.skill_level),
            labelByLevel.get(metric.skill_level),
          ),
        }))
      : base;

  return options
    .filter((option) => Number.isFinite(option.value) && hasText(option.label))
    .map((option) => ({
      value: option.value,
      label: formatSkillLevelLabel(option.value, option.label),
      name: stripLeadingSkillLevelScore(option.label, option.value),
    }));
};

export const getDefaultCustomLabels = (
  typeLabelType: CustomLabelType,
  isClinical: boolean = false,
) => {
  const indexArray = Array.from(
    { length: typeLabelType === CustomLabelType.SKILL_LEVEL_SCORE_EPA ? 5 : 6 },
    (_, i) => i + 1,
  );
  const customLabels = indexArray.map((index) => {
    let label = "";
    if (typeLabelType === CustomLabelType.SKILL_LEVEL_SCORE_EPA) {
      label = LEVEL_EPA_OBJ[index - 1];
    } else if (typeLabelType === CustomLabelType.SKILL_LEVEL_SCORE_COMPETENCY) {
      label = LEVEL_COMPETENCY_OBJ[index - 1];
    } else if (isClinical) {
      label = TARGET_ACHIEVEMENT_TIME_CLINIC[index - 1];
    } else {
      label = TARGET_ACHIEVEMENT_TIME_EDUCATIONAL[index - 1];
    }
    return {
      custom_label_id: `${typeLabelType}-${index}`,
      type: typeLabelType,
      index,
      label,
    };
  });
  return customLabels;
};
